/* eslint-disable no-inline-style */
import * as R from 'ramda';
import { bodyCompositionReadingText, isBodyCompositionReading } from './bodyComposition';
import { roundFloatToFixedDecimal } from 'utilsModule/common';
import {
  VITAL_TYPE_MAPPING,
  VITAL_NAME_MAPPING,
  TASK_STATUS_MAPPING,
  VITAL_DISPLAY_MAPPING,
  UNIT_DISPLAY_MAPPING,
  MEALTIME_MAPPING,
  MEALTIME_NAME_MAPPING
} from 'dataModule/constants/vital';

const getVitalTaskStatus = status => TASK_STATUS_MAPPING[status];

// get vital type
const getVitalType = R.prop(R.__, VITAL_NAME_MAPPING);

// get vital display name
const getVitalName = R.prop(R.__, VITAL_TYPE_MAPPING);

// get Vital Type From Array such as ['BP', 'HR']
const getVitalTypeFromArray = R.compose(
  getVitalType,
  getVitalName,
  R.toUpper,
  R.join('_'),
);
const isMissedReading = R.compose(
  R.complement(R.isNil),
  R.prop('dataAbsentReason'),
);
// get reading from observation collection
const getReading = observation => {
  if (!observation) return null;
  if (isMissedReading(observation)) return 'Missed Reading';
  const { component, valueQuantity } = observation;
  const vitalType = R.pathOr('', ['vitals', 0], observation) || R.pathOr('', ['code', 'text'], observation);

  if (valueQuantity) {
    // For HS round to 2 decimal places
    if (vitalType === 'HS') {
      return `${roundFloatToFixedDecimal(Number(valueQuantity.value), 2).toFixed(2)} ${valueQuantity.unit}`;
    } else if (vitalType === 'HR') {
      return `${roundFloatToFixedDecimal(Number(valueQuantity.value), 0).toFixed()} ${valueQuantity.unit}`;
    }
    return `${roundFloatToFixedDecimal(Number(valueQuantity.value), 1).toFixed(1)} ${valueQuantity.unit}`;
  }
  if (!component) return null;
  // Body Composition: the out-of-range values (or the weight when none), not all nine joined.
  if (isBodyCompositionReading(observation)) return bodyCompositionReadingText(observation);
  const getUnit = R.path([0, 'valueQuantity', 'unit']);
  const getValue = R.path(['valueQuantity', 'value']);
  const unit = getUnit(component);
  let values = R.map(getValue)(component);
  if (vitalType === 'HS') {
    values = values.map(m => roundFloatToFixedDecimal(Number(m), 2).toFixed(2));
  } else if (vitalType === 'HR') {
    values = values.map(m => roundFloatToFixedDecimal(Number(m), 0).toFixed());
  } else {
    values = values.map(m => roundFloatToFixedDecimal(Number(m), 1).toFixed(1));
  }
  return `${values.join('/')} ${unit}`;
};

const getReadingForRender = (observation, vitalLabel = false) => {
  if (!observation) return [];
  if (isMissedReading(observation)) return [{ reading: 'Missed' }];
  const getUnit = R.path(['valueQuantity', 'unit']);
  const getInterpretation = R.path(['interpretation', 'coding', 0, 'code']);
  const getValue = R.path(['valueQuantity', 'value']);
  const { component, valueQuantity } = observation;
  const isNumber = val => /^\d+(\.\d+)*$/.test(val);
  if (valueQuantity) {
    if (vitalLabel) {
      return [
        {
          interpretation: getInterpretation(observation),
          reading: isNumber(getValue(observation)) ? parseFloat(getValue(observation)) : getValue(observation),
          vitalName: VITAL_DISPLAY_MAPPING[R.toLower(vitalLabel)],
          unit: getUnit(observation),
        },
      ];
    }
    return [
      {
        reading: isNumber(getValue(observation)) ? parseFloat(getValue(observation)) : getValue(observation),
      }
    ];
  }
  return R.map(
    c => ({
      interpretation: getInterpretation(c),
      reading: isNumber(getValue(c)) ? parseFloat(getValue(c)) : getValue(c),
      vitalName: VITAL_DISPLAY_MAPPING[R.toLower(R.path(['code', 'coding', 0, 'display'], c))],
      unit: getUnit(c),
    }),
    component,
  );
};

const getReadingForRender2 = (observation, vitalLabel = false) => {
  if (!observation) return null;
  const getVitalName = val => observation.find(e => e.name === val) ? VITAL_DISPLAY_MAPPING[R.toLower(val)] : VITAL_DISPLAY_MAPPING[R.toLower(vitalLabel)];
  const getUnit = R.path(['valueQuantity', 'unit']);
  const getInterpretation = R.path(['interpretation', 'coding', 0, 'code']);
  const getValue = R.path(['valueQuantity', 'value']);
  const isNumber = val => /^\d+(\.\d+)*$/.test(val);

  return R.map(
    c => ({
      interpretation: getInterpretation(c),
      reading: c.valueQuantity === null ? 'Missed' : isNumber(getValue(c)) ? parseFloat(getValue(c)) : getValue(c),
      vitalName: c.name === 'sys' ? 'SYS' : c.name === 'dia' ? 'DIA' : getVitalName(c),
      unit: c.valueQuantity === null ? UNIT_DISPLAY_MAPPING[R.toLower(c.name)] : getUnit(c),
    }),
    observation,
  );
};



// observations: Array
const getReadingFromTask = R.compose(
  R.join(' '),
  R.reject(R.either(R.isNil, R.equals('Missed Reading'))),
  R.map(getReading),
);

/*
 * When traillingZeros is true, .0000 will be returned
 * When it is false, 54.000 will be returned as 54 even if it is fixed to a certain decimal place
 */
/* Body Composition values counted in whole numbers (labels used by no other vital). BMR runs to
   four digits and is grouped with a comma, "1,389", whatever the browser's language. */
const WHOLE_NUMBER_LABELS = ['Basal Metabolic Rate', 'Visceral Fat'];

export const normalizeVitalsDecimals = (rawValue, label, traillingZeros = true) => {
  if (WHOLE_NUMBER_LABELS.includes(label)) {
    return Math.round(Number(rawValue)).toLocaleString('en-US');
  }
  if (traillingZeros) {
    if (label === 'Body Weight') {
      return roundFloatToFixedDecimal(Number(rawValue), 2).toFixed(2);
    }
    if (label === 'Heart Rate') {
      return roundFloatToFixedDecimal(Number(rawValue), 0).toFixed();
    }
    return roundFloatToFixedDecimal(Number(rawValue), 1).toFixed(1);
  } else {
    if (label === 'Body Weight') {
      return roundFloatToFixedDecimal(Number(rawValue), 2);
    }
    if (label === 'Heart Rate') {
      return roundFloatToFixedDecimal(Number(rawValue), 0);
    }
    return roundFloatToFixedDecimal(Number(rawValue), 1);
  }
};

const convertReadingUnit = (unit) => {
  // if(unit === 'beats/min' || unit === 'breaths/min') {
  //   return 'bpm';
  // }

  return unit;
};

const getMealtime = when => MEALTIME_MAPPING[when];

const getMealtimeTaken = when => MEALTIME_NAME_MAPPING[when];

const VITAL_LABEL_TRANSLATION_KEYS = {
  'Blood Glucose': 'common:vitals.vitalTypes.bg',
  'Blood Pressure': 'common:vitals.vitalTypes.bp',
  'Blood Pressure - Diastolic': 'common:vitals.vitalTypes.bp',
  'Blood Pressure - Systolic': 'common:vitals.vitalTypes.bp',
  'Body Temperature': 'common:vitals.vitalTypes.btm',
  'Body Weight': 'common:vitals.vitalTypes.hs',
  'Heart Rate': 'common:vitals.vitalTypes.hr',
  'Oxygen Saturation': 'common:vitals.vitalTypes.po.001',
  'Respiration Rate': 'common:vitals.vitalTypes.rr',
  'Respiratory Rate': 'common:vitals.vitalTypes.rr',
  SpO2: 'common:vitals.vitalTypes.po.002',
  Sp02: 'common:vitals.vitalTypes.po.002',
  Temperature: 'common:vitals.vitalTypes.temperature',
  Weight: 'common:vitals.vitalTypes.weight',
};

const translateVitalDisplay = (label, t) => {
  if (!label || !t) return label;

  const normalizedLabel = VITAL_NAME_MAPPING[label] || label;
  const translationKey = VITAL_LABEL_TRANSLATION_KEYS[normalizedLabel];

  if (translationKey) {
    return t(translationKey);
  }

  // Support code-based labels such as BP, PO_HR, ECG, etc.
  if (VITAL_NAME_MAPPING[label]) {
    return t(`reports:vitalNameMappings.${label}`, { defaultValue: normalizedLabel });
  }

  return t(`reports:${normalizedLabel}`, { defaultValue: normalizedLabel });
};

const convertPeriod = (hours, minutes, seconds, t) => {
  const translate = (key, defaultValue) => {
    if (!t) return defaultValue;
    return t(key, { defaultValue });
  };
  const durations = [];
  if(hours > 0) {
    const convertTime = hours > 1
      ? translate('common:times.hours', 'hours')
      : translate('common:times.hour', 'hour');
    durations.push(`${hours} ${convertTime}`);
  }

  if(minutes > 0) {
    const convertTime = minutes > 1
      ? translate('common:times.minutes', 'minutes')
      : translate('common:times.minute', 'minute');
    durations.push(`${minutes} ${convertTime}`);
  }

  if(seconds > 0) {
    const convertTime = seconds > 1
      ? translate('common:times.seconds', 'seconds')
      : translate('common:times.second', 'second');
    durations.push(`${seconds} ${convertTime}`);
  }
  
  return durations.join(' ');
};

export default {
  getVitalTaskStatus,
  getVitalType,
  getVitalTypeFromArray,
  getVitalName,
  getReading,
  getReadingFromTask,
  getReadingForRender,
  getReadingForRender2,
  normalizeVitalsDecimals,
  getMealtime,
  getMealtimeTaken,
  translateVitalDisplay,
  convertReadingUnit,
  convertPeriod
};
